import { Modal, Form, Input, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import {
  createTemplateContextAction,
  listTemplateContextsAction,
} from '../../../../store/actions/templatecontext.action';
import {
  ENCOUNTER_PHASE_OPTIONS,
  rowsToCriteria,
  type TemplateContextFormValues,
  type CriteriaRow,
} from './templateContext.constants';
import CriteriaFields from './CriteriaFields';

type AddTemplateContextProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  filters?: Record<string, string>;
};

const AddTemplateContext = ({
  isOpen,
  setIsOpen,
  filters,
}: AddTemplateContextProps) => {
  const [form] = Form.useForm<TemplateContextFormValues>();
  const dispatch = useDispatch();

  const handleClose = () => {
    form.resetFields();
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const criteriaRows: CriteriaRow[] = values.criteria ?? [];
    const matchingCriteria = rowsToCriteria(criteriaRows);

    const payload = {
      encounter_phase: values.encounter_phase,
      variable_name: values.variable_name?.trim(),
      template_name: values.template_name?.trim(),
      data: values.data?.trim(),
      matching_criteria: matchingCriteria,
      section_name: values.section_name?.trim(),
    };

    dispatch(
      createTemplateContextAction(payload, () => {
        handleClose();
        dispatch(listTemplateContextsAction(filters));
      }) as any
    );
  };

  return (
    <Modal
      title="Add Template Context"
      width={800}
      open={isOpen}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Create"
      destroyOnClose
    >
      <Form
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        style={{ marginTop: 16 }}
        form={form}
        initialValues={{ criteria: [] }}
      >
        <Form.Item
          label={
            <span>
              Template Name{' '}
              <Tooltip title="Supports partial match — e.g. 'Well' will match 'Well Visit'">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="template_name"
          rules={[{ required: true, message: 'Template name is required' }]}
        >
          <Input placeholder="e.g. Well Visit" />
        </Form.Item>

        <Form.Item
          label="Encounter Phase"
          name="encounter_phase"
          rules={[{ required: true, message: 'Encounter phase is required' }]}
        >
          <Select
            placeholder="Select encounter phase"
            options={ENCOUNTER_PHASE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Section Name{' '}
              <Tooltip title="Exact match, case-insensitive">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="section_name"
          rules={[{ required: true, message: 'Section name is required' }]}
        >
          <Input placeholder="e.g. Assessment" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Variable Name{' '}
              <Tooltip title="Use {{name}} to substitute in template section">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="variable_name"
          rules={[{ required: true, message: 'Variable name is required' }]}
        >
          <Input placeholder="e.g. my_var" />
        </Form.Item>

        <Form.Item
          label="Instructions"
          name="data"
          rules={[{ required: true, message: 'Instructions are required' }]}
        >
          <Input.TextArea
            rows={12}
            placeholder="Template context instructions"
          />
        </Form.Item>

        <CriteriaFields />
      </Form>
    </Modal>
  );
};

export default AddTemplateContext;
